import { Children, isValidElement, type ReactNode } from "react"

import { useTranslation } from "react-i18next"

/** The plain text of a link's children, enough to tell whether they only repeat the address. */
function textOf(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child)
      if (isValidElement<{ children?: ReactNode }>(child)) return textOf(child.props.children)
      return ""
    })
    .join("")
}

type NeutralizedLinkProps = {
  href: string
  children?: ReactNode
}

/**
 * An external link inside a message built from the user's personal sources, shown as plain text with
 * its full address instead of something to click. A mail or chat message can carry a link that was
 * written by somebody else; nothing here navigates, prefetches or sends the reader anywhere.
 */
export function NeutralizedLink({ href, children }: NeutralizedLinkProps) {
  const label = textOf(children).trim()
  const repeatsAddress = label === "" || label === href
  return (
    <span className="break-words" data-testid="neutralized-link">
      {!repeatsAddress && <>{children} (</>}
      <span className="break-all">{href}</span>
      {!repeatsAddress && ")"}
    </span>
  )
}

type BlockedExternalImageProps = {
  alt?: string
}

/** An image from outside the app in a personal-sources message: never loaded, since loading it is a beacon. */
export function BlockedExternalImage({ alt }: BlockedExternalImageProps) {
  const { t } = useTranslation()
  return (
    <span
      className="text-muted-foreground border-border/60 inline-block rounded border border-dashed px-1.5 py-0.5 text-xs"
      data-testid="blocked-external-image"
    >
      {t("personalSources.search.imageRemoved", {
        defaultValue: "Image from an outside address not shown",
      })}
      {alt ? `: ${alt}` : ""}
    </span>
  )
}
